import React, { useState } from 'react';
import { Link } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import Header from '../components/Header';
import Footer from '../components/Footer';
import { submitDealerApplication } from '../lib/api';
import { Handshake, Loader2, CheckCircle2, AlertCircle } from 'lucide-react';

/**
 * Public dealer application form (/dealer). Submissions land in the admin
 * referrals console with status=pending; approval sends the dealer code and
 * dashboard link over WhatsApp.
 */
const DealerApplyPage: React.FC = () => {
  const { t } = useTranslation();
  const [form, setForm] = useState({ name: '', phone: '', email: '', message: '' });
  const [loading, setLoading] = useState(false);
  const [done, setDone] = useState(false);
  const [error, setError] = useState<string | null>(null);

  const handleChange = (e: React.ChangeEvent<HTMLInputElement | HTMLTextAreaElement>) => {
    const { name, value } = e.target;
    setForm((prev) => ({ ...prev, [name]: value }));
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    setLoading(true);
    setError(null);
    try {
      const res = await submitDealerApplication({
        name: form.name.trim(),
        phone: form.phone.trim(),
        email: form.email.trim() || undefined,
        message: form.message.trim() || undefined,
      });
      if (!res.success) {
        throw new Error(res.error?.message || t('referral.applyFailed', '提交失敗，請稍後再試'));
      }
      setDone(true);
    } catch (err) {
      setError(err instanceof Error ? err.message : String(err));
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="min-h-screen bg-brand-paper">
      <Header />
      <main className="pt-24 pb-20 px-4 sm:px-6 lg:px-8">
        <div className="max-w-xl mx-auto">
          <div className="text-center mb-10">
            <div className="w-20 h-20 mx-auto mb-6 flex items-center justify-center rounded-full bg-brand-cinnabar/10">
              <Handshake className="w-10 h-10 text-brand-cinnabar" />
            </div>
            <h1 className="text-3xl md:text-4xl font-serif tracking-widest text-brand-ink mb-4">
              {t('referral.dealerApplyTitle', '成為扶陽堂經銷商')}
            </h1>
            <p className="text-brand-ink/60">
              {t('referral.dealerApplyIntro', '提交申請後，我們的團隊會盡快與您聯絡審批事宜。')}
            </p>
          </div>

          {done ? (
            <div className="bg-white border border-brand-ink/10 p-10 text-center">
              <CheckCircle2 className="w-12 h-12 text-green-600 mx-auto mb-4" />
              <h2 className="text-xl font-serif text-brand-ink mb-2">{t('referral.applyDone', '申請已提交')}</h2>
              <p className="text-brand-ink/60 mb-6">
                {t('referral.applyDoneHint', '審批通過後，您會透過 WhatsApp 收到專屬經銷碼與儀表板連結。')}
              </p>
              <Link to="/" className="text-brand-cinnabar underline underline-offset-4">
                {t('common.backHome', '返回首頁')}
              </Link>
            </div>
          ) : (
            <form onSubmit={handleSubmit} className="bg-white border border-brand-ink/10 p-8 space-y-5">
              {error && (
                <div className="flex items-start gap-2 p-3 bg-red-50 text-red-700 text-sm">
                  <AlertCircle className="w-4 h-4 mt-0.5 flex-shrink-0" />
                  {error}
                </div>
              )}

              <div>
                <label className="block text-sm text-brand-ink/70 mb-2">{t('referral.name', '姓名')} *</label>
                <input
                  type="text"
                  name="name"
                  value={form.name}
                  onChange={handleChange}
                  required
                  className="w-full border border-brand-ink/10 px-4 py-3 focus:outline-none focus:border-brand-cinnabar"
                  placeholder={t('referral.namePlaceholder', '請輸入姓名')}
                />
              </div>

              <div>
                <label className="block text-sm text-brand-ink/70 mb-2">{t('referral.phone', 'WhatsApp 電話')} *</label>
                <input
                  type="tel"
                  name="phone"
                  value={form.phone}
                  onChange={handleChange}
                  required
                  className="w-full border border-brand-ink/10 px-4 py-3 focus:outline-none focus:border-brand-cinnabar"
                  placeholder="852 9123 4567"
                />
              </div>

              <div>
                <label className="block text-sm text-brand-ink/70 mb-2">{t('referral.email', '電郵')}</label>
                <input
                  type="email"
                  name="email"
                  value={form.email}
                  onChange={handleChange}
                  className="w-full border border-brand-ink/10 px-4 py-3 focus:outline-none focus:border-brand-cinnabar"
                  placeholder="example@email.com"
                />
              </div>

              <div>
                <label className="block text-sm text-brand-ink/70 mb-2">
                  {t('referral.applyMessage', '簡介（銷售渠道、經驗等）')}
                </label>
                <textarea
                  name="message"
                  value={form.message}
                  onChange={handleChange}
                  rows={4}
                  maxLength={500}
                  className="w-full border border-brand-ink/10 px-4 py-3 focus:outline-none focus:border-brand-cinnabar resize-none"
                  placeholder={t('referral.applyMessagePlaceholder', '請簡述您的銷售渠道或相關經驗')}
                />
              </div>

              <button
                type="submit"
                disabled={loading}
                className="w-full inline-flex items-center justify-center gap-2 px-6 py-3 bg-brand-cinnabar text-brand-paper text-lg font-medium hover:bg-brand-ink transition-colors disabled:opacity-60"
              >
                {loading && <Loader2 className="w-5 h-5 animate-spin" />}
                {t('referral.submitApply', '提交申請')}
              </button>
            </form>
          )}
        </div>
      </main>
      <Footer />
    </div>
  );
};

export default DealerApplyPage;
